import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Home, Wifi, WifiOff, Power, PowerOff, Thermometer, 
  Droplets, Gauge, Map, Router, Zap, AlertCircle,
  CheckCircle, Clock, Sun, Activity, Settings, ArrowLeft,
  Monitor, Globe, Signal, Timer, Download, Upload, Cpu,
  Play, User, FileText
} from 'lucide-react';
import { cloudqiApi } from '../../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';

const DiagnosticoConexao = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contrato, setContrato] = useState(null);
  const [userData, setUserData] = useState(null);
  const [selectedHome, setSelectedHome] = useState(null);
  const [homeDetails, setHomeDetails] = useState(null);
  const [loadingHomeDetails, setLoadingHomeDetails] = useState(false);
  const [networkData, setNetworkData] = useState(null);

  useEffect(() => {
    fetchContratoData();
  }, [id]);

  const fetchContratoData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/contratos/${id}`);
      setContrato(response.data);
      
      if (response.data.cliente?.email) {
        await fetchClienteCloudQI(response.data.cliente.email);
      }
      
      // Gerar dados mock de rede
      generateNetworkMockData();
    } catch (error) {
      console.error('Erro ao buscar dados do contrato:', error);
      toast.error('Erro ao carregar dados do contrato');
    } finally {
      setLoading(false);
    }
  };

  const fetchClienteCloudQI = async (email) => {
    try {
      const response = await cloudqiApi.getUserByEmail(email);
      setUserData(response.data);
      
      if (response.data.homesSummary?.homes?.length > 0) {
        const firstHome = response.data.homesSummary.homes[0];
        setSelectedHome(firstHome);
        await fetchHomeDetails(firstHome.id);
      }
    } catch (error) {
      console.error('Erro ao buscar dados CloudQI:', error);
      // Não mostra erro pois pode não ter conta CloudQI
    }
  };

  const fetchHomeDetails = async (homeId) => {
    try {
      setLoadingHomeDetails(true);
      const response = await cloudqiApi.getHomeDetailed(homeId);
      setHomeDetails(response.data);
    } catch (error) {
      console.error('Erro ao buscar detalhes da casa:', error);
    } finally {
      setLoadingHomeDetails(false);
    }
  };

  const generateNetworkMockData = () => {
    const currentTime = Date.now();
    const sessionStartTime = currentTime - (Math.random() * 172800000); // Até 2 dias atrás
    const sessionDuration = currentTime - sessionStartTime;
    const sessionRemaining = 86400000 - (sessionDuration % 86400000); // Sessão de 24h
    
    // Dados simulados de conexão
    const mockData = {
      provider: 'Provedor Local',
      plan: `${Math.floor(Math.random() * 500) + 100} Mbps`,
      downloadSpeed: Math.floor(Math.random() * 400) + 50,
      uploadSpeed: Math.floor(Math.random() * 100) + 20,
      latency: Math.floor(Math.random() * 30) + 5,
      jitter: Math.floor(Math.random() * 10) + 1,
      packetLoss: (Math.random() * 2).toFixed(2),
      uptime: 99.2 + Math.random() * 0.7,
      ipPublic: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
      dns: ['8.8.8.8', '8.8.4.4'],
      signalStrength: Math.floor(Math.random() * 40) - 80,
      connectedDevices: Math.floor(Math.random() * 15) + 5,
      bandwidth24: Math.floor(Math.random() * 150) + 50,
      bandwidth5: Math.floor(Math.random() * 400) + 200,
      routerModel: 'Router CloudQI Pro',
      routerFirmware: '2.4.1-stable',
      lastSpeedTest: new Date(Date.now() - Math.random() * 86400000 * 3),
      
      // Dados PPPoE
      pppoe: {
        username: `cliente${Math.floor(Math.random() * 10000)}@provedor.com.br`,
        status: 'connected',
        sessionTime: sessionDuration,
        sessionRemaining: sessionRemaining,
        bandwidthConfigured: `${Math.floor(Math.random() * 300) + 100}/${Math.floor(Math.random() * 50) + 20}`,
        ipAssigned: `10.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 254) + 1}`,
        gateway: `10.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.1`,
        lastReconnect: new Date(sessionStartTime),
        reconnectCount: Math.floor(Math.random() * 5),
      },
      
      // TR-069 Router Management
      tr069: {
        enabled: true,
        acsUrl: 'https://acs.provedor.com.br:7547',
        connectionStatus: 'online',
        lastInform: new Date(Date.now() - Math.random() * 3600000),
        periodicInform: 300, // segundos
        parameterKey: `PK_${Date.now()}`,
        softwareVersion: '1.2.3-TR069',
        hardwareVersion: 'HW_v2.1',
        manufacturerOUI: '00259E',
        productClass: 'CPE_Router',
        serialNumber: `SN${Math.floor(Math.random() * 1000000)}`,
        provisioningCode: `PROV_${Math.floor(Math.random() * 10000)}`,
      },
      
      // Conexão Física (OLT)
      physical: {
        olt: {
          name: `OLT-${Math.floor(Math.random() * 10) + 1}`,
          ip: `192.168.100.${Math.floor(Math.random() * 50) + 10}`,
          model: 'ZTE C320',
          location: 'Central Telecom',
          status: 'online',
          temperature: (Math.random() * 20 + 35).toFixed(1), // 35-55°C
          uptime: Math.floor(Math.random() * 8760) + 100, // horas
        },
        slot: Math.floor(Math.random() * 16) + 1,
        pon: Math.floor(Math.random() * 16) + 1,
        caixa: `CX-${Math.floor(Math.random() * 1000) + 1}`,
        porta: Math.floor(Math.random() * 8) + 1,
        onu: {
          serial: `ZTEG${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
          model: 'F660 V9.0',
          status: 'online',
          distance: (Math.random() * 18 + 0.5).toFixed(2), // km
          rxPower: (Math.random() * -10 - 15).toFixed(2), // dBm
          txPower: (Math.random() * 5 + 0).toFixed(2), // dBm
          temperature: (Math.random() * 30 + 25).toFixed(1), // °C
          voltage: (Math.random() * 0.5 + 3.0).toFixed(2), // V
          onuId: Math.floor(Math.random() * 127) + 1,
          lastRegistration: new Date(Date.now() - Math.random() * 86400000 * 7),
          firmwareVersion: 'V9.0.10P2N1',
          uptime: Math.floor(Math.random() * 168) + 1, // horas
        }
      },
      
      // Dados de Tráfego (últimas 24h)
      traffic: {
        download: Array.from({length: 24}, (_, i) => ({
          hour: i,
          value: Math.floor(Math.random() * 80 + 10) // MB/h
        })),
        upload: Array.from({length: 24}, (_, i) => ({
          hour: i,
          value: Math.floor(Math.random() * 20 + 5) // MB/h
        })),
        totalDownload: Math.floor(Math.random() * 50 + 10), // GB no mês
        totalUpload: Math.floor(Math.random() * 15 + 3), // GB no mês
      },
      
      // Consumo de Recursos
      resources: {
        cpu: Math.floor(Math.random() * 60 + 10), // %
        memory: Math.floor(Math.random() * 40 + 30), // %
        storage: Math.floor(Math.random() * 20 + 15), // %
        temperature: Math.floor(Math.random() * 25 + 35), // °C
        uptime: Math.floor(Math.random() * 720 + 24), // horas
      }
    };
    setNetworkData(mockData);
  };

  const handleHomeSelect = async (home) => {
    setSelectedHome(home);
    await fetchHomeDetails(home.id);
  };

  const isDeviceOnline = (lastUpdatedAgo) => {
    // Extrai o número de minutos/horas/dias da string
    const timeRegex = /(\d+)\s*(minuto|hora|dia)/i;
    const match = lastUpdatedAgo.match(timeRegex);
    
    if (!match) return false;
    
    const value = parseInt(match[1]);
    const unit = match[2].toLowerCase();
    
    // Considera online apenas se atualizado há menos de 10 minutos
    if (unit.includes('minuto')) {
      return value < 10;
    }
    
    // Se foi há horas ou dias, está offline
    return false;
  };

  const getDeviceStatusColor = (lastUpdatedAgo) => {
    return isDeviceOnline(lastUpdatedAgo) ? 'text-green-500' : 'text-red-500';
  };

  const getDeviceStatusIcon = (lastUpdatedAgo) => {
    return isDeviceOnline(lastUpdatedAgo) ? CheckCircle : AlertCircle;
  };

  const getSignalQuality = (rssi) => {
    if (rssi >= -50) return { quality: 'Excelente', color: 'bg-green-500', width: '100%' };
    if (rssi >= -60) return { quality: 'Bom', color: 'bg-green-400', width: '75%' };
    if (rssi >= -70) return { quality: 'Regular', color: 'bg-yellow-500', width: '50%' };
    return { quality: 'Ruim', color: 'bg-red-500', width: '25%' };
  };

  const getSpeedColor = (speed, max) => {
    const percentage = (speed / max) * 100;
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  const formatDuration = (milliseconds) => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    if (days > 0) {
      return `${days}d ${remainingHours}h ${minutes}m`;
    }
    return `${hours}h ${minutes}m`;
  };
  
  const getSignalColor = (value, type) => {
    if (type === 'rx') {
      if (value >= -20) return 'text-green-600';
      if (value >= -25) return 'text-yellow-600';
      return 'text-red-600';
    }
    if (type === 'tx') {
      if (value >= 0) return 'text-green-600';
      if (value >= -3) return 'text-yellow-600';
      return 'text-red-600';
    }
    return 'text-gray-600';
  };
  
  const SimpleChart = ({ data, color = 'blue', height = 60 }) => {
    const max = Math.max(...data.map(d => d.value));
    
    return (
      <div className="flex items-end justify-center space-x-1" style={{ height }}>
        {data.map((item, index) => (
          <div
            key={index}
            className={`bg-${color}-500 rounded-t`}
            style={{
              height: `${(item.value / max) * height}px`,
              width: '8px',
              minHeight: '2px'
            }}
            title={`${item.hour}h: ${item.value}MB`}
          />
        ))}
      </div>
    );
  };

  const getRoomColor = (roomName) => {
    const colors = {
      'Garagem': 'bg-gray-200 dark:bg-gray-700',
      'Escritório': 'bg-blue-200 dark:bg-blue-800',
      'Sala': 'bg-green-200 dark:bg-green-800',
      'Copa': 'bg-yellow-200 dark:bg-yellow-800',
      'Cozinha': 'bg-red-200 dark:bg-red-800',
      'Quarto': 'bg-purple-200 dark:bg-purple-800',
      'Banheiro': 'bg-cyan-200 dark:bg-cyan-800',
      'default': 'bg-gray-200 dark:bg-gray-700'
    };
    
    for (const [key, color] of Object.entries(colors)) {
      if (roomName.toLowerCase().includes(key.toLowerCase())) {
        return color;
      }
    }
    return colors.default;
  };

  // Corrigir contagem de portas para SmartPoint V1
  const correctPortCount = (device) => {
    if (device.version?.includes('cloudqi-smartpoint') || device.version?.includes('cloudqi-smart-point')) {
      // SmartPoint V1 sempre tem 4 portas
      return {
        ...device,
        ports: {
          ...device.ports,
          total: 4,
          details: device.ports.details.slice(0, 4) // Garantir apenas 4 portas
        }
      };
    }
    return device;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Carregando diagnóstico de conexão...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <div className="bg-white dark:bg-dark-card shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/contratos/${id}`)}
                className="mr-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Diagnóstico de Conexão - {contrato?.cliente?.nome}
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-blue-500" />
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Atualizado: {format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dados de Rede do Cliente */}
        {networkData && (
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
              <Globe className="h-6 w-6 mr-2 text-blue-500" />
              Status da Conexão de Internet
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {/* Velocidade Download */}
              <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Download className="h-5 w-5 text-green-600" />
                  <span className={`text-sm font-medium ${getSpeedColor(networkData.downloadSpeed, 500)}`}>
                    {networkData.downloadSpeed} Mbps
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Download</p>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min((networkData.downloadSpeed / 500) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Velocidade Upload */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Upload className="h-5 w-5 text-blue-600" />
                  <span className={`text-sm font-medium ${getSpeedColor(networkData.uploadSpeed, 100)}`}>
                    {networkData.uploadSpeed} Mbps
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Upload</p>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min((networkData.uploadSpeed / 100) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Latência */}
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Timer className="h-5 w-5 text-purple-600" />
                  <span className={`text-sm font-medium ${networkData.latency <= 20 ? 'text-green-600' : networkData.latency <= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {networkData.latency}ms
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Latência</p>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                  <div 
                    className={`h-2 rounded-full transition-all ${networkData.latency <= 20 ? 'bg-green-600' : networkData.latency <= 50 ? 'bg-yellow-600' : 'bg-red-600'}`}
                    style={{ width: `${Math.min((100 - networkData.latency), 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Uptime */}
              <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Signal className="h-5 w-5 text-orange-600" />
                  <span className="text-sm font-medium text-orange-600">
                    {networkData.uptime.toFixed(1)}%
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Uptime</p>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                  <div 
                    className="bg-orange-600 h-2 rounded-full transition-all"
                    style={{ width: `${networkData.uptime}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Informações Técnicas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">Plano Contratado</p>
                <p className="font-semibold text-gray-900 dark:text-white">{networkData.plan}</p>
              </div>
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">IP Público</p>
                <p className="font-semibold text-gray-900 dark:text-white">{networkData.ipPublic}</p>
              </div>
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">Jitter</p>
                <p className="font-semibold text-gray-900 dark:text-white">{networkData.jitter}ms</p>
              </div>
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">Perda de Pacotes</p>
                <p className="font-semibold text-gray-900 dark:text-white">{networkData.packetLoss}%</p>
              </div>
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">Dispositivos Conectados</p>
                <p className="font-semibold text-gray-900 dark:text-white">{networkData.connectedDevices}</p>
              </div>
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">Último Teste</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {format(networkData.lastSpeedTest, 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                </p>
              </div>
            </div>

            {/* Ações de Conectividade */}
            <div className="mt-6 flex flex-wrap gap-3">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Monitor className="h-4 w-4 mr-2" />
                Executar Speed Test
              </button>
              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Signal className="h-4 w-4 mr-2" />
                Teste de DNS
              </button>
              <button className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Globe className="h-4 w-4 mr-2" />
                Alterar DNS
              </button>
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Wifi className="h-4 w-4 mr-2" />
                Verificar Qualidade WiFi
              </button>
              <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Power className="h-4 w-4 mr-2" />
                Reiniciar Roteador
              </button>
            </div>
          </div>
        )}

        {/* Dados PPPoE */}
        {networkData?.pppoe && (
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
              <Router className="h-6 w-6 mr-2 text-purple-500" />
              Sessão PPPoE
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Activity className="h-5 w-5 text-purple-600" />
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    networkData.pppoe.status === 'connected' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    {networkData.pppoe.status === 'connected' ? 'Conectado' : 'Desconectado'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Status da Sessão</p>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">
                    {formatDuration(networkData.pppoe.sessionTime)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Tempo de Sessão</p>
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Timer className="h-5 w-5 text-orange-600" />
                  <span className="text-sm font-medium text-orange-600">
                    {formatDuration(networkData.pppoe.sessionRemaining)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Tempo Restante</p>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Zap className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-600">
                    {networkData.pppoe.bandwidthConfigured} Mbps
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Banda Configurada</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 text-sm">
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">Usuário PPPoE</p>
                <p className="font-semibold text-gray-900 dark:text-white font-mono">{networkData.pppoe.username}</p>
              </div>
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">IP Atribuído</p>
                <p className="font-semibold text-gray-900 dark:text-white font-mono">{networkData.pppoe.ipAssigned}</p>
              </div>
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">Gateway</p>
                <p className="font-semibold text-gray-900 dark:text-white font-mono">{networkData.pppoe.gateway}</p>
              </div>
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">Última Reconexão</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {format(networkData.pppoe.lastReconnect, 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                <p className="text-gray-600 dark:text-gray-400">Reconexões</p>
                <p className="font-semibold text-gray-900 dark:text-white">{networkData.pppoe.reconnectCount} vezes</p>
              </div>
            </div>

            {/* Ações PPPoE */}
            <div className="mt-6 flex flex-wrap gap-3">
              <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Power className="h-4 w-4 mr-2" />
                Desconectar Sessão
              </button>
              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Play className="h-4 w-4 mr-2" />
                Reconectar PPPoE
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Settings className="h-4 w-4 mr-2" />
                Alterar Credenciais
              </button>
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                Resetar Timeout
              </button>
            </div>
          </div>
        )}

        {/* TR-069 Router Management */}
        {networkData?.tr069 && (
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
              <Settings className="h-6 w-6 mr-2 text-indigo-500" />
              Gerenciamento TR-069
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className={`p-4 rounded-lg ${networkData.tr069.enabled ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Status TR-069</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    networkData.tr069.enabled && networkData.tr069.connectionStatus === 'online'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    {networkData.tr069.enabled && networkData.tr069.connectionStatus === 'online' ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Último Inform</p>
                <p className="font-semibold text-blue-700 dark:text-blue-300 text-sm">
                  {format(networkData.tr069.lastInform, 'dd/MM HH:mm', { locale: ptBR })}
                </p>
              </div>
              
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Periodic Inform</p>
                <p className="font-semibold text-purple-700 dark:text-purple-300 text-sm">{networkData.tr069.periodicInform}s</p>
              </div>
              
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Parameter Key</p>
                <p className="font-semibold text-yellow-700 dark:text-yellow-300 text-sm font-mono">
                  {networkData.tr069.parameterKey.slice(-8)}...
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-3">
                <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                  <p className="text-gray-600 dark:text-gray-400">ACS URL</p>
                  <p className="font-semibold text-gray-900 dark:text-white font-mono text-xs">{networkData.tr069.acsUrl}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                  <p className="text-gray-600 dark:text-gray-400">Software Version</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{networkData.tr069.softwareVersion}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                  <p className="text-gray-600 dark:text-gray-400">Hardware Version</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{networkData.tr069.hardwareVersion}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                  <p className="text-gray-600 dark:text-gray-400">Serial Number</p>
                  <p className="font-semibold text-gray-900 dark:text-white font-mono">{networkData.tr069.serialNumber}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                  <p className="text-gray-600 dark:text-gray-400">Product Class</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{networkData.tr069.productClass}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-bg p-3 rounded">
                  <p className="text-gray-600 dark:text-gray-400">Provisioning Code</p>
                  <p className="font-semibold text-gray-900 dark:text-white font-mono">{networkData.tr069.provisioningCode}</p>
                </div>
              </div>
            </div>

            {/* Ações TR-069 */}
            <div className="mt-6 flex flex-wrap gap-3">
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Globe className="h-4 w-4 mr-2" />
                Abrir Interface TR-069
              </button>
              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Forçar Connection Request
              </button>
              <button className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Settings className="h-4 w-4 mr-2" />
                Alterar Configurações
              </button>
              <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Power className="h-4 w-4 mr-2" />
                Reboot Remoto
              </button>
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Download className="h-4 w-4 mr-2" />
                Upgrade Firmware
              </button>
            </div>
          </div>
        )}

        {/* Conexão Física (OLT) */}
        {networkData?.physical && (
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
              <Zap className="h-6 w-6 mr-2 text-red-500" />
              Infraestrutura Física
            </h2>
            
            {/* OLT Information */}
            <div className="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-4">OLT - Optical Line Terminal</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-red-600 dark:text-red-400">Nome/Modelo</p>
                  <p className="font-semibold text-red-900 dark:text-red-200">{networkData.physical.olt.name}</p>
                  <p className="text-xs text-red-700 dark:text-red-300">{networkData.physical.olt.model}</p>
                </div>
                <div>
                  <p className="text-sm text-red-600 dark:text-red-400">IP/Localização</p>
                  <p className="font-semibold text-red-900 dark:text-red-200 font-mono">{networkData.physical.olt.ip}</p>
                  <p className="text-xs text-red-700 dark:text-red-300">{networkData.physical.olt.location}</p>
                </div>
                <div>
                  <p className="text-sm text-red-600 dark:text-red-400">Status/Temperatura</p>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${networkData.physical.olt.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="font-semibold text-red-900 dark:text-red-200 capitalize">{networkData.physical.olt.status}</span>
                  </div>
                  <p className="text-xs text-red-700 dark:text-red-300">Temp: {networkData.physical.olt.temperature}°C</p>
                </div>
                <div>
                  <p className="text-sm text-red-600 dark:text-red-400">Uptime</p>
                  <p className="font-semibold text-red-900 dark:text-red-200">{networkData.physical.olt.uptime}h</p>
                </div>
              </div>
            </div>

            {/* Path Information */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center">
                <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">Slot</p>
                <p className="text-2xl font-bold text-blue-800 dark:text-blue-300">{networkData.physical.slot}</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-center">
                <p className="text-sm text-green-600 dark:text-green-400 mb-1">PON</p>
                <p className="text-2xl font-bold text-green-800 dark:text-green-300">{networkData.physical.pon}</p>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg text-center">
                <p className="text-sm text-yellow-600 dark:text-yellow-400 mb-1">Caixa</p>
                <p className="text-lg font-bold text-yellow-800 dark:text-yellow-300">{networkData.physical.caixa}</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center">
                <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">Porta</p>
                <p className="text-2xl font-bold text-purple-800 dark:text-purple-300">{networkData.physical.porta}</p>
              </div>
            </div>

            {/* ONU Information */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-300 mb-4">ONU - Optical Network Unit</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-green-600 dark:text-green-400">Serial/Modelo</p>
                  <p className="font-semibold text-green-900 dark:text-green-200 font-mono">{networkData.physical.onu.serial}</p>
                  <p className="text-xs text-green-700 dark:text-green-300">{networkData.physical.onu.model}</p>
                </div>
                <div>
                  <p className="text-sm text-green-600 dark:text-green-400">Status/ID</p>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${networkData.physical.onu.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="font-semibold text-green-900 dark:text-green-200 capitalize">{networkData.physical.onu.status}</span>
                  </div>
                  <p className="text-xs text-green-700 dark:text-green-300">ID: {networkData.physical.onu.onuId}</p>
                </div>
                <div>
                  <p className="text-sm text-green-600 dark:text-green-400">Distância</p>
                  <p className="font-semibold text-green-900 dark:text-green-200">{networkData.physical.onu.distance} km</p>
                </div>
                <div>
                  <p className="text-sm text-green-600 dark:text-green-400">Uptime</p>
                  <p className="font-semibold text-green-900 dark:text-green-200">{networkData.physical.onu.uptime}h</p>
                </div>
              </div>

              {/* Signal Information */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-dark-card p-3 rounded border-l-4 border-blue-500">
                  <p className="text-sm text-gray-600 dark:text-gray-400">RX Power</p>
                  <p className={`font-bold text-lg ${getSignalColor(parseFloat(networkData.physical.onu.rxPower), 'rx')}`}>
                    {networkData.physical.onu.rxPower} dBm
                  </p>
                </div>
                <div className="bg-white dark:bg-dark-card p-3 rounded border-l-4 border-green-500">
                  <p className="text-sm text-gray-600 dark:text-gray-400">TX Power</p>
                  <p className={`font-bold text-lg ${getSignalColor(parseFloat(networkData.physical.onu.txPower), 'tx')}`}>
                    {networkData.physical.onu.txPower} dBm
                  </p>
                </div>
                <div className="bg-white dark:bg-dark-card p-3 rounded border-l-4 border-red-500">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Temperatura</p>
                  <p className={`font-bold text-lg ${parseFloat(networkData.physical.onu.temperature) > 70 ? 'text-red-600' : parseFloat(networkData.physical.onu.temperature) > 50 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {networkData.physical.onu.temperature}°C
                  </p>
                </div>
                <div className="bg-white dark:bg-dark-card p-3 rounded border-l-4 border-purple-500">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Tensão</p>
                  <p className={`font-bold text-lg ${parseFloat(networkData.physical.onu.voltage) < 2.8 ? 'text-red-600' : parseFloat(networkData.physical.onu.voltage) < 3.0 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {networkData.physical.onu.voltage}V
                  </p>
                </div>
              </div>

              <div className="mt-4 text-xs text-green-700 dark:text-green-300">
                <p><strong>Último Registro:</strong> {format(networkData.physical.onu.lastRegistration, 'dd/MM/yyyy HH:mm', { locale: ptBR })}</p>
                <p><strong>Firmware:</strong> {networkData.physical.onu.firmwareVersion}</p>
              </div>
            </div>

            {/* Ações de Infraestrutura */}
            <div className="mt-6 flex flex-wrap gap-3">
              <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Zap className="h-4 w-4 mr-2" />
                Verificar Sinais Ópticos
              </button>
              <button className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Settings className="h-4 w-4 mr-2" />
                Acessar OLT
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Monitor className="h-4 w-4 mr-2" />
                Status da PON
              </button>
              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Registrar ONU
              </button>
              <button className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <AlertCircle className="h-4 w-4 mr-2" />
                Resetar ONU
              </button>
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center">
                <Thermometer className="h-4 w-4 mr-2" />
                Monitorar Temperatura
              </button>
            </div>
          </div>
        )}

        {/* Gráficos de Tráfego e Consumo */}
        {networkData?.traffic && networkData?.resources && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Tráfego de Dados */}
            <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                <Activity className="h-6 w-6 mr-2 text-blue-500" />
                Tráfego de Dados (24h)
              </h2>
              
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Download</span>
                    <span className="text-sm text-blue-600 dark:text-blue-400">Total do mês: {networkData.traffic.totalDownload} GB</span>
                  </div>
                  <SimpleChart data={networkData.traffic.download} color="blue" height={60} />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Upload</span>
                    <span className="text-sm text-green-600 dark:text-green-400">Total do mês: {networkData.traffic.totalUpload} GB</span>
                  </div>
                  <SimpleChart data={networkData.traffic.upload} color="green" height={60} />
                </div>
              </div>
            </div>

            {/* Ferramentas de Diagnóstico */}
            <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                <Settings className="h-6 w-6 mr-2 text-orange-500" />
                Ferramentas de Diagnóstico
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Teste de Velocidade */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <Monitor className="h-6 w-6 text-blue-600" />
                    <span className="text-xs text-blue-600 font-medium">SPEED TEST</span>
                  </div>
                  <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Teste de Velocidade</h3>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">Executar teste de velocidade completo</p>
                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                    Executar Teste
                  </button>
                </div>

                {/* Ping Test */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <Signal className="h-6 w-6 text-green-600" />
                    <span className="text-xs text-green-600 font-medium">PING</span>
                  </div>
                  <h3 className="font-semibold text-green-800 dark:text-green-300 mb-2">Teste de Latência</h3>
                  <p className="text-xs text-green-700 dark:text-green-400 mb-3">Ping para servidores específicos</p>
                  <button className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                    Executar Ping
                  </button>
                </div>

                {/* Traceroute */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <Globe className="h-6 w-6 text-purple-600" />
                    <span className="text-xs text-purple-600 font-medium">TRACE</span>
                  </div>
                  <h3 className="font-semibold text-purple-800 dark:text-purple-300 mb-2">Rastreamento de Rota</h3>
                  <p className="text-xs text-purple-700 dark:text-purple-400 mb-3">Traceroute até destino específico</p>
                  <button className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                    Executar Trace
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Soluções Rápidas para Problemas Comuns */}
        <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
            <AlertCircle className="h-6 w-6 mr-2 text-orange-500" />
            Soluções Rápidas
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Internet Lenta */}
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-700">
              <div className="flex items-center mb-3">
                <Timer className="h-5 w-5 text-yellow-600 mr-2" />
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-300">Internet Lenta</h3>
              </div>
              <div className="space-y-2">
                <button className="w-full bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Otimizar Banda
                </button>
                <button className="w-full bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Verificar QoS
                </button>
              </div>
            </div>

            {/* Sem Internet */}
            <div className="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 p-4 rounded-lg border border-red-200 dark:border-red-700">
              <div className="flex items-center mb-3">
                <WifiOff className="h-5 w-5 text-red-600 mr-2" />
                <h3 className="font-semibold text-red-800 dark:text-red-300">Sem Conexão</h3>
              </div>
              <div className="space-y-2">
                <button className="w-full bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Verificar Cabos
                </button>
                <button className="w-full bg-pink-600 hover:bg-pink-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Reset Completo
                </button>
              </div>
            </div>

            {/* WiFi Instável */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
              <div className="flex items-center mb-3">
                <Wifi className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="font-semibold text-blue-800 dark:text-blue-300">WiFi Instável</h3>
              </div>
              <div className="space-y-2">
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Otimizar Canal
                </button>
                <button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Reset WiFi
                </button>
              </div>
            </div>

            {/* PPPoE Desconectando */}
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 rounded-lg border border-purple-200 dark:border-purple-700">
              <div className="flex items-center mb-3">
                <Router className="h-5 w-5 text-purple-600 mr-2" />
                <h3 className="font-semibold text-purple-800 dark:text-purple-300">PPPoE Instável</h3>
              </div>
              <div className="space-y-2">
                <button className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Ajustar MTU
                </button>
                <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Renovar Credenciais
                </button>
              </div>
            </div>

            {/* Sinal Óptico Baixo */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-lg border border-green-200 dark:border-green-700">
              <div className="flex items-center mb-3">
                <Zap className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="font-semibold text-green-800 dark:text-green-300">Sinal Baixo</h3>
              </div>
              <div className="space-y-2">
                <button className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Testar Fibra
                </button>
                <button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Solicitar Visita
                </button>
              </div>
            </div>

            {/* Equipamento Superaquecendo */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 p-4 rounded-lg border border-orange-200 dark:border-orange-700">
              <div className="flex items-center mb-3">
                <Thermometer className="h-5 w-5 text-orange-600 mr-2" />
                <h3 className="font-semibold text-orange-800 dark:text-orange-300">Superaquecimento</h3>
              </div>
              <div className="space-y-2">
                <button className="w-full bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Verificar Ventilação
                </button>
                <button className="w-full bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors">
                  Shutdown Preventivo
                </button>
              </div>
            </div>
          </div>

          {/* Botões de Ação Global */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ações Gerais</h3>
            <div className="flex flex-wrap gap-3">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Gerar Relatório Completo
              </button>
              <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                Marcar como Resolvido
              </button>
              <button className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Agendar Manutenção
              </button>
              <button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                <User className="h-5 w-5 mr-2" />
                Contatar Cliente
              </button>
              <button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                Abrir Chamado Urgente
              </button>
            </div>
          </div>
        </div>

        {/* CloudQI Devices */}
        {userData && (
          <>
            {/* Seletor de Casas */}
            <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                <Home className="h-6 w-6 mr-2 text-green-500" />
                Propriedades CloudQI ({userData.homesSummary.totalHomes})
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {userData.homesSummary.homes.map((home) => (
                  <div
                    key={home.id}
                    onClick={() => handleHomeSelect(home)}
                    className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                      selectedHome?.id === home.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{home.name}</h3>
                      <Home className="h-5 w-5 text-gray-500" />
                    </div>
                    
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <p>{home.rooms.length} cômodos</p>
                      <p>{home.rooms.reduce((total, room) => total + room.deviceCount, 0)} dispositivos</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Representação Visual da Casa */}
            {selectedHome && homeDetails && (
              <div className="bg-white dark:bg-dark-card rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                    <Map className="h-6 w-6 mr-2 text-purple-500" />
                    Planta da Casa: {selectedHome.name}
                  </h2>
                  {loadingHomeDetails && (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  )}
                </div>

                {/* Informações da Casa */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 p-4 bg-gray-50 dark:bg-dark-bg rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Endereço</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{homeDetails.address}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Coordenadas</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {homeDetails.latitude?.toFixed(6)}, {homeDetails.longitude?.toFixed(6)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total de Dispositivos</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{homeDetails.totalDevices}</p>
                  </div>
                </div>

                {/* Planta da Casa */}
                <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg p-8 bg-gray-50 dark:bg-dark-bg">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {homeDetails.rooms.map((room) => {
                      const roomColorClass = getRoomColor(room.name);
                      
                      return (
                        <div
                          key={room.roomId}
                          className={`${roomColorClass} border-2 border-gray-400 dark:border-gray-500 rounded-lg p-4 min-h-[200px] relative`}
                        >
                          {/* Nome do Cômodo */}
                          <div className="text-center mb-3">
                            <h3 className="font-bold text-gray-800 dark:text-gray-200 text-lg">
                              {room.name}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              {room.devices.length} dispositivo(s)
                            </p>
                          </div>

                          {/* Dispositivos no Cômodo */}
                          <div className="space-y-3">
                            {room.devices.length === 0 ? (
                              <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                                <Router className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                <p className="text-xs">Nenhum dispositivo</p>
                              </div>
                            ) : (
                              room.devices.map((device) => {
                                const correctedDevice = correctPortCount(device);
                                const isOnline = isDeviceOnline(device.lastUpdatedAgo);
                                const signalData = device.wifi?.rssi ? getSignalQuality(device.wifi.rssi) : null;
                                
                                return (
                                  <div
                                    key={device.id}
                                    className={`bg-white dark:bg-dark-card rounded-lg p-3 shadow-sm border-l-4 ${
                                      isOnline ? 'border-green-500' : 'border-red-500'
                                    }`}
                                  >
                                    {/* Header do Dispositivo */}
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center">
                                        <div className={`w-2 h-2 rounded-full mr-2 ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                        <span className="font-medium text-xs text-gray-900 dark:text-white">
                                          {device.location}
                                        </span>
                                      </div>
                                      <div className="flex items-center">
                                        <span className={`text-xs px-2 py-1 rounded-full mr-2 ${
                                          isOnline 
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                        }`}>
                                          {isOnline ? 'Online' : 'Offline'}
                                        </span>
                                        <Router className={`h-4 w-4 ${getDeviceStatusColor(device.lastUpdatedAgo)}`} />
                                      </div>
                                    </div>

                                    {/* Informações Técnicas Compactas */}
                                    <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                                      <div className="flex justify-between">
                                        <span>IP:</span>
                                        <span className="font-mono">
                                          {device.ip !== "IP não disponível" ? device.ip : "N/A"}
                                        </span>
                                      </div>
                                      
                                      <div className="flex justify-between">
                                        <span>Uptime:</span>
                                        <span>{device.uptime}</span>
                                      </div>

                                      <div className="flex justify-between">
                                        <span>Última atualização:</span>
                                        <span className={`font-medium ${isOnline ? 'text-green-600' : 'text-red-600'}`}>
                                          {device.lastUpdatedAgo}
                                        </span>
                                      </div>

                                      {/* WiFi */}
                                      {device.wifi?.ssid && (
                                        <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded mt-2">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                              <Wifi className="h-3 w-3 mr-1 text-blue-500" />
                                              <span className="font-medium">{device.wifi.ssid}</span>
                                            </div>
                                            {signalData && (
                                              <div className="flex items-center">
                                                <div className="w-8 bg-gray-200 rounded-full h-1 mr-1">
                                                  <div 
                                                    className={`${signalData.color} h-1 rounded-full`}
                                                    style={{ width: signalData.width }}
                                                  ></div>
                                                </div>
                                                <span className="text-xs">{device.wifi.rssi}dBm</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Sensores */}
                                      {Object.keys(device.sensors).length > 0 && (
                                        <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded mt-2">
                                          <div className="grid grid-cols-2 gap-1 text-xs">
                                            {device.sensors.temperature && (
                                              <div className="flex items-center">
                                                <Thermometer className="h-3 w-3 mr-1 text-red-500" />
                                                <span>{device.sensors.temperature.toFixed(1)}°C</span>
                                              </div>
                                            )}
                                            {device.sensors.humidity && (
                                              <div className="flex items-center">
                                                <Droplets className="h-3 w-3 mr-1 text-blue-500" />
                                                <span>{device.sensors.humidity.toFixed(1)}%</span>
                                              </div>
                                            )}
                                            {device.sensors.luminosity && (
                                              <div className="flex items-center">
                                                <Sun className="h-3 w-3 mr-1 text-yellow-500" />
                                                <span>{device.sensors.luminosity} lux</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Portas (Corrigido para 4 portas max) */}
                                      {correctedDevice.ports.total > 0 && (
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded mt-2">
                                          <div className="flex justify-between items-center mb-1">
                                            <span className="font-medium">Portas:</span>
                                            <span>{correctedDevice.ports.active}/{correctedDevice.ports.total}</span>
                                          </div>
                                          <div className="grid grid-cols-2 gap-1">
                                            {correctedDevice.ports.details.map((port, index) => (
                                              <div key={index} className="flex items-center justify-between text-xs">
                                                <div className="flex items-center">
                                                  <div className={`w-1.5 h-1.5 rounded-full mr-1 ${port.active ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                                                  <span>{port.port}</span>
                                                </div>
                                                {port.dimmerValue !== null && (
                                                  <span className="text-gray-500">
                                                    {Math.round((port.dimmerValue / 255) * 100)}%
                                                  </span>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Automações */}
                                      {device.automations?.hardwareAutomations?.length > 0 && (
                                        <div className="bg-orange-50 dark:bg-orange-900/20 p-2 rounded mt-2">
                                          <div className="flex justify-between items-center">
                                            <span className="font-medium">Automações:</span>
                                            <span>{device.automations.hardwareAutomations.length}</span>
                                          </div>
                                          <div className="space-y-1 mt-1">
                                            {device.automations.hardwareAutomations.slice(0, 2).map((automation, index) => (
                                              <div key={index} className="flex items-center justify-between">
                                                <span className="truncate">{automation.name}</span>
                                                <span className={`px-1 py-0.5 rounded text-xs ${
                                                  automation.status === 1 
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                                                }`}>
                                                  {automation.status === 1 ? 'ON' : 'OFF'}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Caso não tenha dados CloudQI */}
        {!userData && !loading && (
          <div className="bg-white dark:bg-dark-card rounded-lg p-8 shadow-sm text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Nenhum dado CloudQI encontrado
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Este cliente não possui dispositivos CloudQI vinculados ao email: <br />
              <span className="font-mono bg-gray-100 dark:bg-dark-bg px-2 py-1 rounded">
                {contrato?.cliente?.email}
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticoConexao;